/**
 * Pure copy for the registry (docs/ux/registry.md): the outcome words and
 * tones, the short sentence read from the back of the room, the missing
 * column's text, the change history's codes as sentences, the fix verbs.
 * The server's own sentences (readinessSentence, each check's detail) are
 * shown verbatim and never rebuilt here.
 */

import { ALL_REPORT_TYPES, reportTypeLabel, type ReportTypeKind } from '@beam/core/status';
import type { ChipTone } from '@/components/matter/StateChip';
import type { CheckState, DoctorChange, DoctorRecord, FixSection, Readiness, ReadinessCheck, ReadinessOutcome, ReadinessSummary } from '@/app/types';
import { pluralise } from '@/lib/format';

export const OUTCOME_WORD: Record<ReadinessOutcome, string> = { ready: 'Ready', blocked: 'Blocked', not_decided: 'Not decided' };
export const OUTCOME_TONE: Record<ReadinessOutcome, ChipTone> = { ready: 'done', blocked: 'error', not_decided: 'warning' };
export const STATE_WORD: Record<CheckState, string> = { met: 'Met', missing: 'Missing', not_decided: 'Not decided' };

/** The pill's order: the demo's type first, then the assessment family, then the rest (docs/ux/registry.md); every core type is present. */
export const TYPES: readonly ReportTypeKind[] = ['ime', 'fitness_for_work', 'permanent_impairment', 'mva', 'supplementary', 'file_review', 'consultation_advice'].filter((t): t is ReportTypeKind => (ALL_REPORT_TYPES as string[]).includes(t));
export const isType = (v: string | null | undefined): v is ReportTypeKind => (TYPES as readonly string[]).includes(v ?? '');
export const shortType = (t: ReportTypeKind): string => reportTypeLabel(t, 'short');
export const longType = (t: ReportTypeKind): string => reportTypeLabel(t, 'label');

/** The one-line outcome for the chip's neighbour: "Blocked for IME: 2 checks missing." */
export function shortSentence(r: Pick<Readiness, 'outcome' | 'missing' | 'reportType'>): string {
  const t = shortType(r.reportType);
  if (r.outcome === 'ready') return `Ready for ${t}.`;
  if (r.outcome === 'blocked') return `Blocked for ${t}: ${pluralise(r.missing.length, 'check')} missing.`;
  return `Not decided for ${t}: every check Beam controls is met.`;
}

/** The server's refusal sentence, rebuilt identically so the list can show it without a second request. */
export function readinessSentence(reportType: ReportTypeKind, r: ReadinessSummary): string {
  const label = longType(reportType);
  if (r.outcome === 'ready') return `Ready for ${label}.`;
  if (r.outcome === 'blocked') return `Blocked for ${label}: ${r.missing.length} ${r.missing.length === 1 ? 'item is' : 'items are'} missing: ${r.missing.join(', ')}.${r.notDecided.length > 0 ? ` Not decided: ${r.notDecided.join(', ')} (ADR-0001).` : ''}`;
  return `Not decided for ${label}: ${r.notDecided.join(', ')} (ADR-0001). Every other check is met.`;
}

/** The MISSING cell: the missing labels, then the undecided ones. */
export function missingText(r: ReadinessSummary): string {
  const parts: string[] = [];
  if (r.missing.length > 0) parts.push(r.missing.join(', '));
  if (r.notDecided.length > 0) parts.push(`not decided: ${r.notDecided.join(', ')}`);
  return parts.join('; ') || 'every check met';
}

/** "Other types: MVA blocked (template verified, ...) . FFW, PI not offered". */
export function otherTypesText(readiness: Record<ReportTypeKind, Readiness>, current: ReportTypeKind, offered: ReportTypeKind[]): string {
  const others = TYPES.filter((t) => t !== current);
  const shown = others.filter((t) => offered.includes(t)).map((t) => {
    const r = readiness[t];
    if (r.outcome === 'blocked') return `${shortType(t)} blocked (${r.missing.join(', ')})`;
    return `${shortType(t)} ${OUTCOME_WORD[r.outcome].toLowerCase()}`;
  });
  const unoffered = others.filter((t) => !offered.includes(t)).map(shortType);
  const parts = [...shown];
  if (unoffered.length > 0) parts.push(`${unoffered.join(', ')} not offered`);
  return parts.join(' · ');
}

/**
 * Where a live-template check is fixed: the templates registry (ADR-0001), not a
 * panel on the doctor. The statutory forms live on the templates page; a
 * missing base is authored in the type's editor.
 */
export function templatesHref(check: ReadinessCheck, reportType: ReportTypeKind): string {
  return /\bAPIA\d/.test(check.detail) ? '/registry/templates' : `/registry/templates/${reportType}`;
}

/** The verb on a check's Fix link, by the section it opens. */
export function fixVerb(check: ReadinessCheck, reportType: ReportTypeKind): string {
  if (check.state === 'not_decided') return 'Verify';
  switch (check.fix) {
    case 'signature': return 'Attach';
    case 'display': return 'Add';
    case 'templates': return 'Open Templates';
    case 'reportTypes': return `Offer ${shortType(reportType)}`;
    case 'status': return 'Activate';
    default: return 'Set';
  }
}

/** Which panel a fix section opens. */
export const PANEL_FOR: Record<FixSection, 'doctor' | 'report' | 'templates'> = { status: 'doctor', details: 'doctor', display: 'report', signature: 'report', templates: 'templates', reportTypes: 'templates' };

const FIELD_WORDS: Record<string, string> = {
  name: 'name', title: 'title', displayName: 'display name', specialty: 'specialty', specialtyGroup: 'specialty group', clinics: 'clinics',
  'signature.blockText': 'signature block', 'display.bio': 'bio', 'display.credentialLine': 'credential line', 'workspace.keyterms': 'key terms',
  'credentials.ahpra': 'AHPRA number', 'credentials.apia': 'APIA number', 'credentials.qualificationsLine': 'qualifications',
  'practice.name': 'the practice name', 'practice.address': 'the address', 'practice.phone': 'the phone number', 'practice.email': 'the email address',
};

/** The change code as a sentence; an unknown code renders as itself so nothing is hidden. */
export function changeSentence(code: string): string {
  if (code === 'seeded') return 'Created from the seed';
  if (code === 'created') return 'Created';
  const upload = /^template_uploaded:([a-z_]+):v(\d+)$/.exec(code);
  if (upload && isType(upload[1])) return `Uploaded ${shortType(upload[1])} template version ${upload[2]}`;
  const sig = /^signature_attached:v(\d+)$/.exec(code);
  if (sig) return `Added signature image version ${sig[1]}`;
  const photo = /^photo_attached:v(\d+)$/.exec(code);
  if (photo) return `Added photo version ${photo[1]}`;
  const band = /^(letterhead|footer)_attached:v(\d+)$/.exec(code);
  if (band) return `Added ${band[1] === 'footer' ? 'footer strip' : 'letterhead'} version ${band[2]}`;
  const form = /^form_uploaded:v(\d+)$/.exec(code);
  if (form) return `Uploaded the form, version ${form[1]}`;
  const parts = code.split(', ').map((part) => {
    if (part === 'activated') return 'marked active';
    if (part === 'deactivated') return 'marked inactive';
    if (part.startsWith('workspace.dictationMode:')) return `dictation mode to ${part.split(':')[1]}`;
    if (part.startsWith('workspace.proofreading:')) return `proofreading ${part.split(':')[1]}`;
    if (part.startsWith('reportTypes')) {
      const added = /:added ([a-z_ ]+)/.exec(part)?.[1]?.split(' ').filter(isType).map(shortType);
      const removed = /:removed ([a-z_ ]+)/.exec(part)?.[1]?.split(' ').filter(isType).map(shortType);
      const bits: string[] = [];
      if (added?.length) bits.push(`now offers ${added.join(', ')}`);
      if (removed?.length) bits.push(`no longer offers ${removed.join(', ')}`);
      return bits.join('; ') || 'report types';
    }
    return FIELD_WORDS[part] ? `changed ${FIELD_WORDS[part]}` : part;
  });
  const sentence = parts.join(', ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/** Every template upload in the history for one report type, newest first: the version list. */
export function templateVersions(audit: DoctorChange[], reportType: ReportTypeKind, current?: { version: number; uploadedAt: string; uploadedBy: string } | null): Array<{ version: number; at: string; byName: string }> {
  const rows = audit
    .map((c) => ({ c, m: /^template_uploaded:([a-z_]+):v(\d+)$/.exec(c.change) }))
    .filter((x) => x.m && x.m[1] === reportType)
    .map((x) => ({ version: Number(x.m![2]), at: x.c.at, byName: x.c.byName ?? x.c.by }));
  // A seeded template has no upload entry of its own: the seed's entry stands for its version 1, and the pointer for whatever is current.
  const seeded = audit.find((c) => c.change === 'seeded');
  if (current && seeded && !rows.some((r) => r.version === 1)) rows.push({ version: 1, at: seeded.at, byName: seeded.byName ?? seeded.by });
  if (current && !rows.some((r) => r.version === current.version)) rows.push({ version: current.version, at: current.uploadedAt, byName: current.uploadedBy === 'system:seed' ? 'Seed data' : current.uploadedBy });
  return rows.sort((a, b) => b.version - a.version);
}

export const DICTATION_WORD = { platform: 'Platform', bridge: 'Bridge' } as const;
export const DICTATION_DETAIL = { platform: 'Deepgram, live in the workspace', bridge: 'Quality Scribe: audio in, completed template back' } as const;

export const TITLES = ['Dr', 'Professor', 'Associate Professor', 'Mr', 'Ms'] as const;

/** "Not set" in the muted colour is the read grid's word for an empty value; never blank. */
export const NOT_SET = 'Not set';

export function metaLine(d: DoctorRecord): string[] {
  return [d.specialty || 'No specialty', d.active ? 'Active' : 'Inactive', `${DICTATION_WORD[d.workspace.dictationMode]} dictation`, d.reportTypes.length > 0 ? `Offers ${d.reportTypes.map(shortType).join(', ')}` : 'Offers no report type'];
}
